/**
 * The typed postMessage protocol between the play shell and a hosted game.
 *
 * This module is the single definition of that wire format. The shell imports
 * it too (`apps/public-web/src/lib/sdk-bridge.ts` re-exports from here) so the
 * two ends cannot describe the message differently — a drift that postMessage
 * would report as silence rather than as an error, because an unrecognised
 * message simply never matches.
 *
 * HOSTED authentication runs on window identity, not origin. A sandboxed document has
 * an opaque origin, so `event.origin` arrives as the string `"null"` and proves
 * nothing; the shell instead requires `event.source` to be the exact
 * `contentWindow` it created. The `channel` field is not a security control —
 * it only keeps unrelated libraries' messages from being parsed as ours.
 *
 * The game addresses its replies to `expectedParentOrigin` and never to `'*'`,
 * because these messages carry a score submission and a wildcard would hand it
 * to whatever happened to be embedding the frame.
 */
export const SDK_CHANNEL = 'tryportal.sdk';
/** External HTTPS frames use a document-bound port, never legacy window traffic.
 * These handshake messages carry no token. BOOTSTRAP follows ACK on the port;
 * application traffic is enabled only after the SDK sends READY on that port. */
export const EXTERNAL_BRIDGE_PROTOCOL = 'MESSAGE_PORT_V1';
export const EXTERNAL_PORT_OFFER = 'EXTERNAL_PORT_OFFER';
export const EXTERNAL_PORT_ACK = 'EXTERNAL_PORT_ACK';
function isChannelMessage(data) {
    return (typeof data === 'object' &&
        data !== null &&
        data['channel'] === SDK_CHANNEL);
}
const GAME_TO_SHELL_TYPES = new Set([
    'READY',
    'SUBMIT_SCORE',
    'SCORE_SUBMITTED',
    'GAME_OVER',
    'ERROR',
    'SANDBOX_PROBE',
    'ACTION_ACK',
    'CREATE_SHARE_CARD',
    'GET_LEADERBOARD',
    'STREAM_MODE_REQUEST',
    'STREAM_SETTINGS_OPEN',
    'CREATOR_VOICE_OPEN',
]);
const SHELL_TO_GAME_TYPES = new Set([
    'BOOTSTRAP',
    'SCORE_RESULT',
    'ACTION',
    'SESSION_STATE',
    'SHARE_CARD_RESULT',
    'LEADERBOARD_RESULT',
    'STREAM_MODE_RESULT',
    'CREATOR_VOICE_RESULT',
]);
export function parseGameMessage(data) {
    if (!isChannelMessage(data))
        return undefined;
    const type = data['type'];
    return typeof type === 'string' && GAME_TO_SHELL_TYPES.has(type)
        ? data
        : undefined;
}
export function parseShellMessage(data) {
    if (!isChannelMessage(data))
        return undefined;
    const type = data['type'];
    return typeof type === 'string' && SHELL_TO_GAME_TYPES.has(type)
        ? data
        : undefined;
}
